import { JsDateRange, toDate } from "common";
import { Dayjs } from "dayjs";
import { PaymentMethod, TransactionType } from "types";
import { AnalyticsFilter } from "../stores/reports/analyticsFilterStore";
import { EarningsReportFilter } from "../stores/reports/earningsReportFilterStore";
import { purchaseTypes } from "./Transaction";

/**
 * Get the initial date values for a particular report. We always know that if a user selects
 * two dates it is from start of the date to the end of that day.
 * Since our date pickers take JS date objects
 */
export const getInitialDate = (from: Dayjs, to: Dayjs): JsDateRange => {
    // Convert the dayjs to a string date so that when you do new Date(), it's in the same day
    // TODO: Into a helper
    const [fromDate, toDate_] = [toDate(from.startOf("day")), toDate(to.endOf("day"), false)];

    return { from: fromDate, to: toDate_ };
};

interface TransactionFilterProps {
    filters: Pick<AnalyticsFilter, "mode" | "products">;
    format?: string;
}

export const getNewTransactionParams = (
    dateParams: any,
    filterParams: TransactionFilterProps,
    ...additionalParams: object[]
) => {
    const { filters, format } = filterParams;

    const params = { "type[in]": purchaseTypes.join(","), format, ...dateParams };
    if (filters.products.selected.length > 0) {
        params["items.orderItem.product[in]"] = filters.products.selected.join(",");
    }

    // merge all "additionalParams" objects into single object
    // since lint/prettier complains about using array.reduce, do it manually.
    let reducedAdditionalParams = {};
    for (const param of additionalParams) {
        reducedAdditionalParams = {
            ...reducedAdditionalParams,
            ...param,
        };
    }

    return { ...params, ...reducedAdditionalParams };
};

export const getEarningReportTransactionsParams = (
    dateParams: Record<string, string>,
    filters: EarningsReportFilter,
    format?: "xlsx",
) => {
    const { paymentMethods, reservationist } = filters;
    // There are 2 ach(manual payout) and ACH(auto payout) payment methods,
    // but we only want to show to not create any confusion for users
    // but need to handle both payment methods when selected/deselected
    const hasAch = paymentMethods.selected.includes(PaymentMethod.ach);
    const hasACH = paymentMethods.selected.includes(PaymentMethod.ACH);
    if (!hasAch && hasACH) {
        paymentMethods.selected.push(PaymentMethod.ach);
    } else if (!hasACH && hasAch) {
        paymentMethods.selected = paymentMethods.selected.filter((method) => method !== PaymentMethod.ach);
    }

    const methodParams = paymentMethods.selected.length > 0 ? { "method[in]": paymentMethods.selected.join(",") } : {};
    const reservationistParams =
        reservationist.selected.length > 0 ? { "createdBy[in]": reservationist.selected.join(",") } : {};

    // Earnings reports always includes disputes
    const typeParams = {
        "type[in]": [...purchaseTypes, TransactionType.DISPUTE, TransactionType.STRIPE_PAYDOWN].join(","),
    };
    return getNewTransactionParams(dateParams, { filters, format }, methodParams, reservationistParams, typeParams);
};
